import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../lib/api';

type ViewState =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'confirm'; contactName: string; orgName: string }
  | { kind: 'already'; contactName: string; orgName: string; date: string }
  | { kind: 'done'; contactName: string; orgName: string };

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    dir="rtl"
    style={{
      minHeight: '100vh',
      background: '#f8fafc',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 16px',
      fontFamily: '-apple-system, system-ui, "Segoe UI", Roboto, sans-serif',
    }}
  >
    <div
      style={{
        maxWidth: 480,
        width: '100%',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 16,
        padding: 28,
        boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
        color: '#1f2937',
      }}
    >
      {children}
    </div>
  </div>
);

const MarketingOptOutPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ kind: 'invalid' });
      return;
    }
    api
      .get(`/public/marketing-opt-out/${encodeURIComponent(token)}`, {
        headers: { Accept: 'application/json' },
      })
      .then(res => {
        const d = res.data || {};
        if (d.alreadyOptedOut) {
          setState({
            kind: 'already',
            contactName: d.contactName || '',
            orgName: d.orgName || '',
            date: d.optedOutAt ? new Date(d.optedOutAt).toLocaleDateString('he-IL') : '',
          });
        } else {
          setState({
            kind: 'confirm',
            contactName: d.contactName || '',
            orgName: d.orgName || '',
          });
        }
      })
      .catch(() => setState({ kind: 'invalid' }));
  }, [token]);

  const onConfirm = async () => {
    if (!token || state.kind !== 'confirm') return;
    setSubmitting(true);
    try {
      await api.post(`/public/marketing-opt-out/${encodeURIComponent(token)}`, null, {
        headers: { Accept: 'application/json' },
      });
      setState({ kind: 'done', contactName: state.contactName, orgName: state.orgName });
    } catch {
      setState({ kind: 'invalid' });
    } finally {
      setSubmitting(false);
    }
  };

  if (state.kind === 'loading') {
    return (
      <Card>
        <p style={{ textAlign: 'center', color: '#6b7280' }}>טוען…</p>
      </Card>
    );
  }

  if (state.kind === 'invalid') {
    return (
      <Card>
        <h1 style={{ fontSize: 22, margin: '0 0 12px' }}>קישור לא תקין</h1>
        <p style={{ lineHeight: 1.6, color: '#374151' }}>
          הקישור הזה כבר לא בתוקף או פגום. אם הגעת לכאן בטעות, אפשר לסגור את הדף.
        </p>
      </Card>
    );
  }

  if (state.kind === 'already') {
    return (
      <Card>
        <h1 style={{ fontSize: 22, margin: '0 0 12px' }}>כבר הוסרת מהדיוור</h1>
        <p style={{ lineHeight: 1.6, color: '#374151' }}>
          שלום {state.contactName}, הבקשה שלך כבר נרשמה
          {state.date ? ` (${state.date})` : ''}. לא נשלח לך דיוור נוסף מ-{state.orgName}.
        </p>
      </Card>
    );
  }

  if (state.kind === 'done') {
    return (
      <Card>
        <h1 style={{ fontSize: 22, margin: '0 0 12px' }}>הוסרת מהדיוור ✓</h1>
        <p style={{ lineHeight: 1.6, color: '#374151' }}>
          שלום {state.contactName}, סימנו אותך כ"לא לקבל הודעות מרקטינג" אצל {state.orgName}.
          אם זה היה בטעות — אפשר ליצור איתנו קשר ונחזיר אותך.
        </p>
      </Card>
    );
  }

  // confirm
  return (
    <Card>
      <h1 style={{ fontSize: 22, margin: '0 0 12px' }}>הסרה מדיוור</h1>
      <p style={{ lineHeight: 1.6, color: '#374151' }}>
        שלום {state.contactName}, האם אתה רוצה להפסיק לקבל מ-{state.orgName} הודעות מרקטינג (מייל, WhatsApp ו-SMS)?
      </p>
      <button
        type="button"
        onClick={onConfirm}
        disabled={submitting}
        style={{
          background: '#dc2626',
          color: '#fff',
          border: 0,
          borderRadius: 10,
          padding: '12px 22px',
          fontSize: 15,
          fontWeight: 600,
          cursor: submitting ? 'not-allowed' : 'pointer',
          marginTop: 14,
          opacity: submitting ? 0.7 : 1,
        }}
      >
        {submitting ? 'מסמן…' : 'כן, הסר אותי'}
      </button>
      <p style={{ color: '#6b7280', fontSize: 13, marginTop: 20 }}>
        תוכל תמיד לפנות אלינו ולחזור לדיוור.
      </p>
    </Card>
  );
};

export default MarketingOptOutPage;
